import React, { useState, useMemo } from 'react';
import {
  FiHelpCircle,
  FiSearch,
  FiChevronDown,
} from 'react-icons/fi';
import { Page, LayoutComposer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './FAQPage.module.css';

const FAQ_DATA = [
  // ── General & Platform Mechanics ──
  {
    id: 'what-is-chinadolphins',
    category: 'general',
    categoryLabel: 'General & Mechanics',
    question: 'What is ChinaDolphins?',
    answer: (
      <>
        <p>
          ChinaDolphins is a next-generation B2B sourcing marketplace designed to help small and medium businesses, e-commerce sellers, and wholesalers access factory-direct manufacturing in China.
        </p>
        <p>
          Our proprietary <strong>Order Aggregation Engine</strong> allows multiple international buyers to combine their purchasing requirements so they can collectively reach Factory Minimum Order Quantities (MOQs) and unlock volume-tier discounts.
        </p>
      </>
    ),
  },
  {
    id: 'how-aggregation-works',
    category: 'general',
    categoryLabel: 'General & Mechanics',
    question: 'How does the Order Aggregation Engine work?',
    answer: (
      <>
        <p>
          When you select a product, you enter the specific quantity your business requires. If your requested quantity is below the factory&apos;s standard MOQ, you may choose to join an active order aggregation pool for that product.
        </p>
        <p>
          Other buyers around the world join the same pool. When the combined purchasing volume reaches the factory&apos;s production threshold, the final lower pricing tier is calculated, and all participating buyers receive updated confirmed quotations.
        </p>
      </>
    ),
  },
  {
    id: 'failed-moq',
    category: 'general',
    categoryLabel: 'General & Mechanics',
    question: 'What happens if an aggregation does not meet the MOQ?',
    answer: (
      <>
        <p>
          If the required MOQ is not reached before the aggregation deadline expires, the pool closes automatically without moving into production.
        </p>
        <p>
          Depending on your preferences, you may be offered the option to join a new aggregation cycle or choose an alternative sourcing solution. Any pre-authorized or pending amounts are cancelled or refunded through the approved payment provider according to our failed-pool policy.
        </p>
      </>
    ),
  },

  // ── Payments & Security ──
  {
    id: 'money-protected',
    category: 'payments',
    categoryLabel: 'Payments & Security',
    question: 'Is my money protected?',
    answer: (
      <>
        <p>
          Yes. ChinaDolphins utilizes approved, regulated third-party payment providers combined with structured commercial milestone verification.
        </p>
        <p>
          Payments are processed through licensed payment institutions, and disbursements are authorized only after defined commercial milestones (such as on-site QC inspection sign-off and China Customs Export clearance) are formally verified. ChinaDolphins does not act as a bank or escrow institution; all financial transactions are processed by approved payment providers.
        </p>
      </>
    ),
  },
  {
    id: 'payment-methods',
    category: 'payments',
    categoryLabel: 'Payments & Security',
    question: 'What payment methods are supported?',
    answer: (
      <>
        <p>
          Our international payment infrastructure supports multi-currency business settlements including:
        </p>
        <ul>
          <li><strong>ACH Direct Debit</strong> for US Buyers</li>
          <li><strong>SEPA Bank Transfers</strong> for European Buyers</li>
          <li><strong>International Corporate Wire (SWIFT)</strong> in USD, EUR, and GBP</li>
          <li>Approved corporate multi-currency settlement channels</li>
        </ul>
      </>
    ),
  },

  // ── Shipping & QC ──
  {
    id: 'ensure-quality',
    category: 'shipping',
    categoryLabel: 'Shipping & QC',
    question: 'How do you ensure product quality?',
    answer: (
      <>
        <p>
          Every order on ChinaDolphins is assigned a verified local <strong>Sourcing Agent</strong> in China.
        </p>
        <p>
          The Sourcing Agent visits the factory, oversees production milestones, and is responsible for conducting a rigorous pre-shipment Quality Control (QC) inspection. High-resolution inspection photos, test logs, and packaging verifications are provided to the buyer prior to dispatch.
        </p>
      </>
    ),
  },
  {
    id: 'customs-logistics',
    category: 'shipping',
    categoryLabel: 'Shipping & QC',
    question: 'Who handles customs and international logistics?',
    answer: (
      <>
        <p>
          <strong>China Side:</strong> The assigned Sourcing Agent, Factory, or approved Exporter of Record manages all Chinese export procedures, customs declarations, and Bill of Lading generation.
        </p>
        <p>
          <strong>Destination Side:</strong> The Buyer acts as the Importer of Record in their destination country and is responsible for applicable local import duties, tariffs, and customs clearances unless a DDP arrangement has been explicitly agreed upon.
        </p>
      </>
    ),
  },
  {
    id: 'direct-factory-comm',
    category: 'shipping',
    categoryLabel: 'Shipping & QC',
    question: 'Can I communicate directly with the Factory?',
    answer: (
      <>
        <p>
          No. To prevent disintermediation, fraud, and miscommunication, all buyer communication is handled directly through your assigned verified Sourcing Agent and the ChinaDolphins platform.
        </p>
        <p>
          Factory identities and direct contact details remain protected, while your local Sourcing Agent eliminates language barriers, manages technical specs, and enforces quality standards on your behalf.
        </p>
      </>
    ),
  },
];

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openItems, setOpenItems] = useState({ 'what-is-chinadolphins': true });

  const toggleItem = id => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const filteredFaqs = useMemo(() => {
    return FAQ_DATA.filter(item => {
      const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
      const matchesSearch =
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.categoryLabel.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeCategory]);

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title="Frequently Asked Questions (FAQ) | ChinaDolphins"
      description="Find answers to common questions about ChinaDolphins Order Aggregation, payment & transaction security, QC inspections, and cross-border shipping."
      className={css.root}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Hero & Search ── */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroBadge}>
                      <FiHelpCircle /> Help Center &amp; FAQs
                    </div>
                    <h1 className={css.heroTitle}>Frequently Asked Questions</h1>
                    <p className={css.heroSubtitle}>
                      Everything you need to know about our sourcing mechanics, aggregation pools, payment security, and quality control.
                    </p>

                    <div className={css.searchBox}>
                      <FiSearch className={css.searchIcon} />
                      <input
                        type="text"
                        placeholder="Search questions (e.g. aggregation, payments, QC, customs)..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className={css.searchInput}
                      />
                    </div>
                  </div>
                </section>

                {/* ── FAQ Content & Tabs ── */}
                <section className={css.section}>
                  <div className={css.tabsContainer}>
                    <button
                      type="button"
                      className={`${css.tabBtn} ${activeCategory === 'all' ? css.tabBtnActive : ''}`}
                      onClick={() => setActiveCategory('all')}
                    >
                      All Questions
                    </button>
                    <button
                      type="button"
                      className={`${css.tabBtn} ${activeCategory === 'general' ? css.tabBtnActive : ''}`}
                      onClick={() => setActiveCategory('general')}
                    >
                      General &amp; Aggregation
                    </button>
                    <button
                      type="button"
                      className={`${css.tabBtn} ${activeCategory === 'payments' ? css.tabBtnActive : ''}`}
                      onClick={() => setActiveCategory('payments')}
                    >
                      Payments &amp; Security
                    </button>
                    <button
                      type="button"
                      className={`${css.tabBtn} ${activeCategory === 'shipping' ? css.tabBtnActive : ''}`}
                      onClick={() => setActiveCategory('shipping')}
                    >
                      Shipping &amp; QC
                    </button>
                  </div>

                  {filteredFaqs.length === 0 ? (
                    <div className={css.noResults}>
                      <p>No questions matched your search &quot;{searchQuery}&quot;.</p>
                      <button
                        type="button"
                        className={css.tabBtn}
                        onClick={() => {
                          setSearchQuery('');
                          setActiveCategory('all');
                        }}
                      >
                        Reset Search
                      </button>
                    </div>
                  ) : (
                    <div className={css.faqList}>
                      {filteredFaqs.map(item => {
                        const isOpen = !!openItems[item.id];
                        return (
                          <div
                            key={item.id}
                            className={`${css.faqItem} ${isOpen ? css.faqItemOpen : ''}`}
                          >
                            <button
                              type="button"
                              className={css.faqHeaderBtn}
                              onClick={() => toggleItem(item.id)}
                              aria-expanded={isOpen}
                            >
                              <div>
                                <span className={css.faqCategoryTag}>{item.categoryLabel}</span>
                                <h3 className={css.faqQuestion}>{item.question}</h3>
                              </div>
                              <FiChevronDown
                                className={`${css.chevronIcon} ${isOpen ? css.chevronIconOpen : ''}`}
                              />
                            </button>
                            {isOpen && <div className={css.faqBody}>{item.answer}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

export default FAQPage;
